import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import cn from 'classnames';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import './OfficeTeam.scss';

import { OfficeContext } from './Office';
import { getOfficeTeamMembers, getOfficeRoster, getUserOfficeSchedule } from '../../api/office';
import { updateAdminScopes } from '../../api/admin_scopes';
import { getSmartImage } from '../../helpers/helpers';
import { OFFICE_SCOPE_LEVELS, getAdditiveScopesForLevel, canManageOffice, formatTimeClean, formatName } from '../../helpers/office_helpers';

const SCOPE_OPTIONS = [
  { value: '', label: 'No Access' },
  ...OFFICE_SCOPE_LEVELS.slice()
    .reverse()
    .map(l => ({ value: l.scope, label: l.label }))
];

const FILTER_OPTIONS = [
  { value: '', label: 'All Levels' },
  ...OFFICE_SCOPE_LEVELS.map(l => ({ value: l.scope, label: l.label })),
  { value: 'none', label: 'No Access' }
];

const getUserOfficeLevel = scopes => {
  if (!scopes) return '';
  for (const level of OFFICE_SCOPE_LEVELS) {
    if (scopes.includes(level.scope)) return level.scope;
  }
  return '';
};

const OfficeTeam = () => {
  const ctx = useContext(OfficeContext);
  const { currentUser, selectedDate, selectedDayLabel, bookingVersion } = ctx;
  const canEditScopes = canManageOffice(currentUser);
  const [roster, setRoster] = useState([]);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [scheduleCache, setScheduleCache] = useState({});
  const [loadingScheduleId, setLoadingScheduleId] = useState(null);
  const [updatingScope, setUpdatingScope] = useState(null);
  const [scopeFilter, setScopeFilter] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    loadRoster();
  }, [selectedDate, bookingVersion]);
  useEffect(() => {
    setScheduleCache({});
  }, [selectedDate, bookingVersion]);

  const loadRoster = async () => {
    try {
      const resp = await getOfficeRoster(selectedDate);
      setRoster(resp?.users || []);
    } catch {
      /* silent */
    }
  };

  const rosterIds = new Set(roster.map(u => u.id));

  const filtered = ctx.allUsers.filter(u => {
    if (search && !u.name?.toLowerCase().includes(search.toLowerCase()) && !u.email?.toLowerCase().includes(search.toLowerCase())) return false;
    if (scopeFilter === 'none') return !getUserOfficeLevel(u.scopes);
    if (scopeFilter) return getUserOfficeLevel(u.scopes) === scopeFilter;
    return true;
  });

  const toggleExpand = useCallback(
    async person => {
      if (expandedId === person.id) {
        setExpandedId(null);
        return;
      }
      setExpandedId(person.id);
      if (scheduleCache[person.id]) return;
      setLoadingScheduleId(person.id);
      try {
        const weekStart =
          moment(selectedDate)
            .startOf('isoWeek')
            .format('YYYY-MM-DD') + 'T00:00:00';
        const weekEnd =
          moment(selectedDate)
            .endOf('isoWeek')
            .format('YYYY-MM-DD') + 'T23:59:59';
        const resp = await getUserOfficeSchedule(person.id, weekStart, weekEnd);
        setScheduleCache(prev => ({ ...prev, [person.id]: resp?.bookings || [] }));
      } catch {
        setScheduleCache(prev => ({ ...prev, [person.id]: [] }));
      } finally {
        setLoadingScheduleId(null);
      }
    },
    [expandedId, scheduleCache, selectedDate]
  );

  const handleScopeChange = async (person, newLevel) => {
    setUpdatingScope(person.id);
    try {
      const existingScopes = person.scopes
        ? person.scopes
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        : [];
      const officeScopes = OFFICE_SCOPE_LEVELS.map(l => l.scope);
      const nonOfficeScopes = existingScopes.filter(s => !officeScopes.includes(s));
      const newOfficeScopes = newLevel ? getAdditiveScopesForLevel(newLevel) : [];
      const finalList = [...nonOfficeScopes, ...newOfficeScopes].filter(Boolean);
      if (!finalList.length) {
        window.ALERT.error('Cannot remove all scopes. The user must retain at least one admin scope.');
        setUpdatingScope(null);
        return;
      }
      await updateAdminScopes(person.id, finalList.join(','));
      await ctx.loadTeamUsers();
      window.ALERT.success(`Updated ${formatName(person.name)} to ${newLevel ? SCOPE_OPTIONS.find(o => o.value === newLevel)?.label : 'No Access'}`);
    } catch (err) {
      window.ALERT.error(err || 'Failed to update scope.');
    } finally {
      setUpdatingScope(null);
    }
  };

  const weekDays = [];
  const weekStart = moment(selectedDate).startOf('isoWeek');
  for (let i = 0; i < 5; i++) weekDays.push(weekStart.clone().add(i, 'days'));

  return (
    <div className='office-team'>
      <div className='team-header'>
        <div className='team-search-wrap' onClick={() => searchRef.current?.focus()}>
          <FontAwesomeIcon icon={faSearch} className='team-search-icon' />
          <input
            ref={searchRef}
            className='team-search'
            type='text'
            placeholder='Search team...'
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className='team-scope-filter' value={scopeFilter} onChange={e => setScopeFilter(e.target.value)}>
          {FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <div className='team-count'>
          {filtered.length} {filtered.length === 1 ? 'person' : 'people'}
        </div>
      </div>

      <div className='team-table'>
        <div className='team-table-header'>
          <div className='th-person'>Person</div>
          <div className='th-status'>Status</div>
          {canEditScopes && <div className='th-access'>Access</div>}
          <div className='th-expand' />
        </div>

        {filtered.length === 0 && <div className='team-empty'>No users found</div>}

        {filtered.map(person => {
          const officeLevel = getUserOfficeLevel(person.scopes);
          const inToday = rosterIds.has(person.id);
          const img = getSmartImage(person.image);
          const isExpanded = expandedId === person.id;
          const schedule = scheduleCache[person.id];
          const isLoadingSched = loadingScheduleId === person.id;

          return (
            <div key={person.id} className={cn('team-row-wrap', { expanded: isExpanded })}>
              <div className='team-row' onClick={() => toggleExpand(person)}>
                <div className='td-person'>
                  {img ? (
                    <img src={img} alt='' className='row-avatar' />
                  ) : (
                    <div className='row-avatar placeholder'>{person.name?.charAt(0) || '?'}</div>
                  )}
                  <div className='row-identity'>
                    <span className='row-name'>{formatName(person.name)}</span>
                    <span className='row-email'>{person.email}</span>
                  </div>
                </div>

                <div className='td-status'>
                  {inToday ? (
                    <span className='status-badge in'>In {selectedDayLabel.toLowerCase()}</span>
                  ) : (
                    <span className='status-badge out'>—</span>
                  )}
                </div>

                {canEditScopes && (
                  <div className='td-access' onClick={e => e.stopPropagation()}>
                    <select
                      className='scope-select'
                      value={officeLevel}
                      onChange={e => handleScopeChange(person, e.target.value)}
                      disabled={updatingScope === person.id}
                    >
                      {SCOPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className='td-expand'>
                  <FontAwesomeIcon icon={faChevronDown} className={cn('expand-icon', { open: isExpanded })} />
                </div>
              </div>

              {isExpanded && (
                <div className='team-row-detail'>
                  {isLoadingSched && <div className='detail-loading'>Loading schedule...</div>}

                  {!isLoadingSched && schedule && (
                    <>
                      <div className='detail-week'>
                        {weekDays.map(day => {
                          const dayStr = day.format('YYYY-MM-DD');
                          const dayBookings = schedule.filter(b => moment(b.starts_at).format('YYYY-MM-DD') === dayStr);
                          const isSelected = dayStr === selectedDate;
                          return (
                            <div key={dayStr} className={cn('week-day', { active: dayBookings.length > 0, selected: isSelected })}>
                              <div className='week-day-label'>{day.format('ddd')}</div>
                              <div className='week-day-bar'>{dayBookings.length > 0 && <div className='week-day-fill' />}</div>
                              {dayBookings.map((b, i) => (
                                <div key={i} className='week-day-booking'>
                                  {b.space?.name || 'Booking'}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>

                      {schedule.length > 0 && (
                        <div className='detail-bookings'>
                          {schedule.map(b => (
                            <div key={b.id} className='booking-row'>
                              <span className='bk-date'>{moment(b.starts_at).format('ddd MMM D')}</span>
                              <span className='bk-space'>{b.space?.name}</span>
                              <span className='bk-time'>
                                {formatTimeClean(b.starts_at)} – {formatTimeClean(b.ends_at)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {schedule.length === 0 && <div className='detail-empty'>No bookings this week</div>}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OfficeTeam;
